import React, { useEffect, useMemo, useRef, useState } from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faArrowLeft,
  faChevronLeft,
  faChevronRight,
  faEye,
  faPlus,
  faTrash,
  faXmark,
} from '@fortawesome/free-solid-svg-icons'
import { getAuth, onAuthStateChanged } from 'firebase/auth'
import {
  arrayUnion,
  collection,
  deleteDoc,
  doc,
  getDocs,
  increment,
  limit,
  onSnapshot,
  orderBy,
  query,
  runTransaction,
  setDoc,
  where,
} from 'firebase/firestore'
import { deleteObject, getDownloadURL, ref as storageRef, uploadBytesResumable } from 'firebase/storage'
import swal from 'sweetalert'
import { db as firestoreDb, storage } from '../firebase'
import sampleImage from '../Images/defprofile.png'
import './WorkspaceUpdatesPage.css'
import { useNavigate } from 'react-router-dom'

const STATUS_COLORS = ['#0F766E', '#1D4ED8', '#7C3AED', '#B45309', '#BE123C']
const STATUS_TTL_MS = 24 * 60 * 60 * 1000
const MAX_STATUS_VIDEO_BYTES = 40 * 1024 * 1024
const DEFAULT_TEXT_BACKGROUND = '#1F2937'

const firstNonEmpty = (...values) => {
  for (const value of values) {
    if (value === null || value === undefined) {
      continue
    }
    const text = String(value).trim()
    if (text) {
      return text
    }
  }
  return ''
}

const readMillis = (value) => {
  if (!value) {
    return 0
  }
  if (typeof value === 'number') {
    return value
  }
  if (value?.toMillis) {
    return value.toMillis()
  }
  if (value?.seconds) {
    return value.seconds * 1000
  }
  return 0
}

const normalizeColor = (value) => {
  const candidate = firstNonEmpty(value, STATUS_COLORS[0])
  return /^#[0-9A-Fa-f]{6}$/.test(candidate) ? candidate : STATUS_COLORS[0]
}

const getFileExtension = (file) => {
  const name = String(file?.name || '').trim()
  const match = name.match(/\.([a-zA-Z0-9]+)$/)
  if (match) {
    return match[1].toLowerCase()
  }
  const mime = String(file?.type || '').trim().toLowerCase()
  if (mime.includes('/')) {
    return mime.split('/')[1] || 'jpg'
  }
  return 'jpg'
}

const formatStatusTime = (timestampMs) => {
  if (!timestampMs) {
    return 'Just now'
  }
  const diff = Date.now() - timestampMs
  if (diff < 60 * 1000) {
    return 'Just now'
  }
  if (diff < 60 * 60 * 1000) {
    const minutes = Math.max(1, Math.floor(diff / (60 * 1000)))
    return `${minutes}m ago`
  }
  if (diff < 24 * 60 * 60 * 1000) {
    const hours = Math.max(1, Math.floor(diff / (60 * 60 * 1000)))
    return `${hours}h ago`
  }
  return new Date(timestampMs).toLocaleDateString('en-US', {
    day: '2-digit',
    month: 'short',
    hour: 'numeric',
    minute: 'numeric',
  })
}

const formatStoryTime = (timestampMs) =>
  timestampMs
    ? new Date(timestampMs).toLocaleTimeString('en-US', {
        hour: 'numeric',
        minute: '2-digit',
      })
    : 'Just now'

const getRelativeMeta = (story) => {
  if (!story) {
    return 'Just now'
  }
  const activeCount = Math.max(1, Number(story.activeCount || 1))
  const viewLabel =
    story.viewCount <= 0
      ? '0 views'
      : story.viewCount === 1
        ? '1 view'
        : `${story.viewCount} views`
  return `${formatStatusTime(story.createdAtMs)} • ${viewLabel}${activeCount > 1 ? ` • ${activeCount} updates` : ''}`
}

const resolveProfile = (authUser, userDocData, userId) => {
  const displayName = firstNonEmpty(
    userDocData?.bio,
    userDocData?.displayName,
    userDocData?.userName,
    authUser?.displayName,
    authUser?.email,
    'Student'
  )
  const course = firstNonEmpty(userDocData?.course, '')
  const avatarUrl = firstNonEmpty(userDocData?.profilePic, authUser?.photoURL, sampleImage)
  return {
    userId,
    displayName,
    course,
    avatarUrl,
  }
}

const buildStory = (documentSnapshot, currentUserId) => {
  const data = documentSnapshot.data() || {}
  const userId = firstNonEmpty(data.userId, documentSnapshot.id)
  const viewerIds = Array.isArray(data.viewerIds) ? data.viewerIds : []
  return {
    statusId: documentSnapshot.id,
    userId,
    displayName: firstNonEmpty(data.displayName, 'Student'),
    course: firstNonEmpty(data.course, ''),
    avatarUrl: firstNonEmpty(data.avatarUrl, sampleImage),
    caption: firstNonEmpty(data.caption, ''),
    mediaUrl: firstNonEmpty(data.mediaUrl, ''),
    mediaPath: firstNonEmpty(data.mediaPath, ''),
    mediaType: firstNonEmpty(data.mediaType, data.mediaUrl ? 'image' : 'text'),
    backgroundColor: normalizeColor(data.backgroundColor),
    createdAtMs: readMillis(data.createdAtMs),
    expiresAtMs: readMillis(data.expiresAtMs),
    viewCount: Number(data.viewCount || 0),
    activeCount: 1,
    isMine: userId === currentUserId,
    isViewedByMe: viewerIds.includes(currentUserId),
  }
}

const buildFeedData = (documents, currentUserId) => {
  const latestPerUser = new Map()
  let myStatus = null

  const now = Date.now()
  ;(documents || []).forEach((documentSnapshot) => {
    const story = buildStory(documentSnapshot, currentUserId)
    if (story.expiresAtMs && story.expiresAtMs <= now) {
      return
    }

    if (story.isMine) {
      if (myStatus == null) {
        myStatus = story
      } else {
        myStatus = {
          ...myStatus,
          activeCount: myStatus.activeCount + 1,
        }
      }
      return
    }

    const existing = latestPerUser.get(story.userId)
    if (existing == null) {
      latestPerUser.set(story.userId, story)
      return
    }

    latestPerUser.set(story.userId, {
      ...existing,
      activeCount: existing.activeCount + 1,
    })
  })

  const recentStatuses = Array.from(latestPerUser.values()).sort(
    (left, right) => right.createdAtMs - left.createdAtMs
  )

  return {
    myStatus,
    recentStatuses,
  }
}

function WorkspaceUpdatesPage() {
  const auth = getAuth()
  const navigate = useNavigate()
  const fileInputRef = useRef(null)
  const selectedMediaUrlRef = useRef('')
  const viewerInitialScrollRef = useRef(true)
  const viewedStoryIdsRef = useRef(new Set())

  const [currentUserId, setCurrentUserId] = useState('')
  const [currentProfile, setCurrentProfile] = useState(null)
  const [loadingFeed, setLoadingFeed] = useState(true)
  const [feedError, setFeedError] = useState('')
  const [feedData, setFeedData] = useState({ myStatus: null, recentStatuses: [] })

  const [composerOpen, setComposerOpen] = useState(false)
  const [composerCaption, setComposerCaption] = useState('')
  const [composerColorIndex, setComposerColorIndex] = useState(0)
  const [selectedMediaFile, setSelectedMediaFile] = useState(null)
  const [selectedMediaType, setSelectedMediaType] = useState('')
  const [selectedMediaPreviewUrl, setSelectedMediaPreviewUrl] = useState('')
  const [publishing, setPublishing] = useState(false)
  const [publishProgress, setPublishProgress] = useState(0)

  const [viewerOpen, setViewerOpen] = useState(false)
  const [viewerUserId, setViewerUserId] = useState('')
  const [viewerInitialStatusId, setViewerInitialStatusId] = useState('')
  const [viewerStories, setViewerStories] = useState([])
  const [viewerIndex, setViewerIndex] = useState(0)
  const [viewerLoading, setViewerLoading] = useState(false)

  const [viewersOpen, setViewersOpen] = useState(false)
  const [viewersLoading, setViewersLoading] = useState(false)
  const [viewersStatus, setViewersStatus] = useState(null)
  const [viewers, setViewers] = useState([])

  const currentStory = useMemo(() => viewerStories[viewerIndex] || null, [viewerStories, viewerIndex])

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (!user) {
        navigate('/login', { replace: true })
        return
      }

      setCurrentUserId(user.uid)
    })

    return () => unsubscribe()
  }, [auth, navigate])

  useEffect(() => {
    if (!currentUserId) {
      return
    }

    let isMounted = true
    const profileQuery = query(
      collection(firestoreDb, 'users'),
      where('userId', '==', currentUserId),
      limit(1)
    )

    getDocs(profileQuery)
      .then((snapshot) => {
        if (!isMounted) {
          return
        }
        if (snapshot.empty) {
          setCurrentProfile(resolveProfile(auth.currentUser, {}, currentUserId))
          return
        }

        const data = snapshot.docs[0].data() || {}
        setCurrentProfile(resolveProfile(auth.currentUser, data, currentUserId))
      })
      .catch(() => {
        if (!isMounted) {
          return
        }
        setCurrentProfile(resolveProfile(auth.currentUser, {}, currentUserId))
      })
      .finally(() => {
        // Nothing else to do here; the feed listener will drive the UI state.
      })

    return () => {
      isMounted = false
    }
  }, [currentUserId])

  useEffect(() => {
    if (!currentUserId) {
      return undefined
    }

    setLoadingFeed(true)
    setFeedError('')
    const feedQuery = query(
      collection(firestoreDb, 'workspaceStatuses'),
      orderBy('createdAtMs', 'desc'),
      limit(300)
    )

    const unsubscribe = onSnapshot(
      feedQuery,
      (snapshot) => {
        const nextFeed = buildFeedData(snapshot.docs, currentUserId)
        setFeedData(nextFeed)
        setLoadingFeed(false)
      },
      () => {
        setFeedError('We could not load stories right now.')
        setLoadingFeed(false)
      }
    )

    return () => unsubscribe()
  }, [currentUserId])

  useEffect(() => {
    if (!composerOpen) {
      return undefined
    }

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        closeComposer()
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [composerOpen])

  useEffect(() => {
    if (!viewerOpen) {
      return undefined
    }

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        closeViewer()
      }
      if (event.key === 'ArrowLeft') {
        goToPreviousStory()
      }
      if (event.key === 'ArrowRight') {
        goToNextStory()
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [viewerOpen, viewerIndex, viewerStories.length])

  useEffect(() => {
    if (!selectedMediaPreviewUrl) {
      return undefined
    }

    return () => {
      if (selectedMediaPreviewUrl.startsWith('blob:')) {
        URL.revokeObjectURL(selectedMediaPreviewUrl)
      }
    }
  }, [selectedMediaPreviewUrl])

  useEffect(() => {
    if (!viewerOpen || !viewerUserId || !currentUserId) {
      return undefined
    }

    setViewerLoading(true)
    const viewerQuery = query(
      collection(firestoreDb, 'workspaceStatuses'),
      where('userId', '==', viewerUserId)
    )

    const unsubscribe = onSnapshot(
      viewerQuery,
      (snapshot) => {
        const stories = snapshot.docs
          .map((docSnapshot) => buildStory(docSnapshot, currentUserId))
          .filter((story) => !story.expiresAtMs || story.expiresAtMs > Date.now())
          .sort((left, right) => right.createdAtMs - left.createdAtMs)

        setViewerStories(stories)
        setViewerLoading(false)
      },
      () => {
        setViewerStories([])
        setViewerLoading(false)
      }
    )

    return () => unsubscribe()
  }, [currentUserId, viewerOpen, viewerUserId])

  useEffect(() => {
    if (!viewerOpen) {
      viewerInitialScrollRef.current = true
      setViewerStories([])
      setViewerIndex(0)
      return
    }

    viewerInitialScrollRef.current = true
  }, [viewerOpen, viewerUserId, viewerInitialStatusId])

  useEffect(() => {
    if (!viewerOpen || !viewerStories.length) {
      return
    }

    if (!viewerInitialScrollRef.current) {
      return
    }

    const initialIndex = viewerStories.findIndex((story) => story.statusId === viewerInitialStatusId)
    setViewerIndex(initialIndex >= 0 ? initialIndex : 0)
    viewerInitialScrollRef.current = false
  }, [viewerOpen, viewerStories, viewerInitialStatusId])

  useEffect(() => {
    if (!currentStory || !viewerOpen || !currentUserId) {
      return
    }

    if (currentStory.isMine) {
      return
    }

    if (viewedStoryIdsRef.current.has(currentStory.statusId)) {
      return
    }

    const viewerName = firstNonEmpty(currentProfile?.displayName, 'Student')
    const viewerAvatar = firstNonEmpty(currentProfile?.avatarUrl, sampleImage)
    const viewerCourse = firstNonEmpty(currentProfile?.course, '')

    const statusRef = doc(firestoreDb, 'workspaceStatuses', currentStory.statusId)
    const viewerRef = doc(firestoreDb, 'workspaceStatuses', currentStory.statusId, 'views', currentUserId)

    runTransaction(firestoreDb, async (transaction) => {
      const viewerSnapshot = await transaction.get(viewerRef)
      if (viewerSnapshot.exists()) {
        return
      }

      transaction.update(statusRef, {
        viewerIds: arrayUnion(currentUserId),
        viewCount: increment(1),
        lastViewedAtMs: Date.now(),
      })

      transaction.set(viewerRef, {
        viewerId: currentUserId,
        displayName: viewerName,
        avatarUrl: viewerAvatar,
        course: viewerCourse,
        viewedAtMs: Date.now(),
      })
    })
      .then(() => {
        viewedStoryIdsRef.current.add(currentStory.statusId)
      })
      .catch(() => {
        // If the update fails we just keep the story visible.
      })
  }, [currentProfile, currentStory, currentUserId, viewerOpen])

  const activeMyStatus = feedData.myStatus
  const recentStatuses = feedData.recentStatuses

  const closeComposer = () => {
    setComposerOpen(false)
    setPublishing(false)
    setPublishProgress(0)
    setComposerCaption('')
    setComposerColorIndex(0)
    setSelectedMediaFile(null)
    setSelectedMediaType('')
    if (selectedMediaUrlRef.current.startsWith('blob:')) {
      URL.revokeObjectURL(selectedMediaUrlRef.current)
    }
    selectedMediaUrlRef.current = ''
    setSelectedMediaPreviewUrl('')
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }

  const openComposer = () => {
    setComposerOpen(true)
  }

  const openStatusViewer = (statusId, userId) => {
    setViewerUserId(userId)
    setViewerInitialStatusId(statusId)
    setViewerOpen(true)
  }

  const closeViewer = () => {
    setViewerOpen(false)
    setViewerUserId('')
    setViewerInitialStatusId('')
    setViewerStories([])
    setViewerIndex(0)
    setViewerLoading(false)
  }

  const goToPreviousStory = () => {
    setViewerIndex((value) => Math.max(0, value - 1))
  }

  const goToNextStory = () => {
    setViewerIndex((value) => Math.min(Math.max(0, viewerStories.length - 1), value + 1))
  }

  const openViewers = async () => {
    if (!currentStory || !currentStory.isMine) {
      return
    }

    setViewersStatus(currentStory)
    setViewersLoading(true)
    setViewersOpen(true)
    setViewers([])

    try {
      const viewersQuery = query(
        collection(firestoreDb, 'workspaceStatuses', currentStory.statusId, 'views'),
        orderBy('viewedAtMs', 'desc')
      )
      const snapshot = await getDocs(viewersQuery)
      setViewers(
        snapshot.docs.map((documentSnapshot) => ({
          id: documentSnapshot.id,
          viewerId: firstNonEmpty(documentSnapshot.data()?.viewerId, documentSnapshot.id),
          displayName: firstNonEmpty(documentSnapshot.data()?.displayName, 'Student'),
          avatarUrl: firstNonEmpty(documentSnapshot.data()?.avatarUrl, sampleImage),
          course: firstNonEmpty(documentSnapshot.data()?.course, ''),
          viewedAtMs: readMillis(documentSnapshot.data()?.viewedAtMs),
        }))
      )
    } catch (error) {
      setViewers([])
    } finally {
      setViewersLoading(false)
    }
  }

  const closeViewers = () => {
    setViewersOpen(false)
    setViewersStatus(null)
    setViewers([])
    setViewersLoading(false)
  }

  const handleMediaSelection = (event) => {
    const file = event.target.files && event.target.files[0]
    if (!file) {
      return
    }

    const fileType = String(file.type || '').toLowerCase()
    const isVideo = fileType.startsWith('video/')
    if (isVideo && file.size > MAX_STATUS_VIDEO_BYTES) {
      swal('Video too large', 'Please choose a video under 40MB.', 'warning')
      event.target.value = ''
      return
    }

    if (selectedMediaUrlRef.current.startsWith('blob:')) {
      URL.revokeObjectURL(selectedMediaUrlRef.current)
    }

    const previewUrl = URL.createObjectURL(file)
    selectedMediaUrlRef.current = previewUrl
    setSelectedMediaFile(file)
    setSelectedMediaType(isVideo ? 'video' : 'image')
    setSelectedMediaPreviewUrl(previewUrl)
  }

  const triggerMediaPicker = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click()
    }
  }

  const clearSelectedMedia = () => {
    if (selectedMediaUrlRef.current.startsWith('blob:')) {
      URL.revokeObjectURL(selectedMediaUrlRef.current)
    }
    selectedMediaUrlRef.current = ''
    setSelectedMediaFile(null)
    setSelectedMediaType('')
    setSelectedMediaPreviewUrl('')
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }

  const publishTextStatus = async () => {
    const userId = currentUserId.trim()
    if (!userId) {
      return
    }

    const caption = composerCaption.trim()
    const hasCaption = caption.length > 0
    const hasMedia = Boolean(selectedMediaFile)
    if (!hasCaption && !hasMedia) {
      swal('Empty status', 'Write a caption or add a photo/video first.', 'warning')
      return
    }

    const profile = currentProfile || resolveProfile(auth.currentUser, {}, userId)
    const statusColor = normalizeColor(STATUS_COLORS[composerColorIndex] || STATUS_COLORS[0])
    const nowMs = Date.now()
    const statusRef = doc(collection(firestoreDb, 'workspaceStatuses'))

    setPublishing(true)
    setPublishProgress(0)

    try {
      if (hasMedia) {
        const extension = getFileExtension(selectedMediaFile)
        const mediaPath = `workspaceStatuses/${userId}/${nowMs}_${userId}.${extension}`
        const uploadRef = storageRef(storage, mediaPath)
        const uploadTask = uploadBytesResumable(uploadRef, selectedMediaFile)

        const mediaUrl = await new Promise((resolve, reject) => {
          uploadTask.on(
            'state_changed',
            (snapshot) => {
              const progress = snapshot.totalBytes
                ? Math.round((snapshot.bytesTransferred / snapshot.totalBytes) * 100)
                : 0
              setPublishProgress(progress)
            },
            reject,
            async () => {
              try {
                const url = await getDownloadURL(uploadTask.snapshot.ref)
                resolve(url)
              } catch (error) {
                reject(error)
              }
            }
          )
        })

        await setDoc(statusRef, {
          statusId: statusRef.id,
          userId,
          displayName: profile.displayName,
          course: profile.course,
          avatarUrl: profile.avatarUrl,
          caption,
          mediaUrl,
          mediaPath,
          mediaType: selectedMediaType || 'image',
          backgroundColor: statusColor,
          createdAtMs: nowMs,
          expiresAtMs: nowMs + STATUS_TTL_MS,
          viewerIds: [],
          viewCount: 0,
          lastViewedAtMs: 0,
        })
      } else {
        await setDoc(statusRef, {
          statusId: statusRef.id,
          userId,
          displayName: profile.displayName,
          course: profile.course,
          avatarUrl: profile.avatarUrl,
          caption,
          mediaUrl: '',
          mediaPath: '',
          mediaType: 'text',
          backgroundColor: statusColor,
          createdAtMs: nowMs,
          expiresAtMs: nowMs + STATUS_TTL_MS,
          viewerIds: [],
          viewCount: 0,
          lastViewedAtMs: 0,
        })
      }

      swal('Status posted', 'Your update is now live.', 'success')
      closeComposer()
    } catch (error) {
      swal('Could not post status', 'We could not publish your update right now.', 'error')
    } finally {
      setPublishing(false)
      setPublishProgress(0)
    }
  }

  const confirmAndDeleteCurrentStory = () => {
    if (!currentStory || !currentStory.isMine) {
      return
    }

    swal({
      title: 'Delete this status?',
      text: 'This will remove the story for everyone.',
      icon: 'warning',
      buttons: ['Cancel', 'Delete'],
      dangerMode: true,
    }).then(async (confirmed) => {
      if (!confirmed) {
        return
      }

      try {
        if (currentStory.mediaPath) {
          await deleteObject(storageRef(storage, currentStory.mediaPath))
        }
      } catch (error) {
        // We still continue deleting the Firestore document.
      }

      try {
        await deleteDoc(doc(firestoreDb, 'workspaceStatuses', currentStory.statusId))
        swal('Status deleted', 'Your update was removed.', 'success')
        closeViewer()
      } catch (error) {
        swal('Delete failed', 'We could not delete that status.', 'error')
      }
    })
  }

  const myStatusBody = useMemo(() => {
    if (!activeMyStatus) {
      return 'Tap to add an update'
    }

    const timeLabel = formatStatusTime(activeMyStatus.createdAtMs)
    const viewLabel =
      activeMyStatus.viewCount <= 0
        ? '0 views'
        : activeMyStatus.viewCount === 1
          ? '1 view'
          : `${activeMyStatus.viewCount} views`
    return `${timeLabel} • ${viewLabel}`
  }, [activeMyStatus])

  const openStatusCard = () => {
    if (!activeMyStatus) {
      openComposer()
      return
    }

    openStatusViewer(activeMyStatus.statusId, activeMyStatus.userId)
  }

  const renderStoryContent = (story, isPreview = false) => {
    if (!story) {
      return null
    }

    if (story.mediaType === 'image' && story.mediaUrl) {
      return (
        <img
          className={`workspaceStatusStoryMedia ${isPreview ? 'workspaceStatusStoryMediaPreview' : ''}`}
          src={story.mediaUrl}
          alt={story.caption || story.displayName}
        />
      )
    }

    if (story.mediaType === 'video' && story.mediaUrl) {
      return (
        <video
          className={`workspaceStatusStoryMedia ${isPreview ? 'workspaceStatusStoryMediaPreview' : ''}`}
          src={story.mediaUrl}
          controls
          autoPlay
          muted
          playsInline
        />
      )
    }

    return (
      <div
        className={`workspaceStatusTextStory ${isPreview ? 'workspaceStatusTextStoryPreview' : ''}`}
        style={{ background: story.backgroundColor }}
      >
        <div className="workspaceStatusTextStoryGlow" />
        <div className="workspaceStatusTextStoryBody">
          <span className="workspaceStatusTextStoryBadge">Status</span>
          <p>{story.caption || ' '}</p>
        </div>
      </div>
    )
  }

  const selectedViewerStory = currentStory

  return (
    <div className="workspaceUpdatesPage">
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*,video/*"
        style={{ display: 'none' }}
        onChange={handleMediaSelection}
      />

      <header className="workspaceUpdatesHeader">
        <button
          type="button"
          className="workspaceUpdatesBackButton"
          onClick={() => navigate('/workspace')}
          aria-label="Back to workspace"
        >
          <FontAwesomeIcon icon={faArrowLeft} />
        </button>
        <div className="workspaceUpdatesHeaderCopy">
          <span>Workspace</span>
          <h1>Status updates</h1>
          <p>Stories last 24 hours, just like the mobile app.</p>
        </div>
        <button
          type="button"
          className="workspaceUpdatesComposeButton"
          onClick={openComposer}
          aria-label="Create status"
        >
          <FontAwesomeIcon icon={faPlus} />
        </button>
      </header>

      <main className="workspaceUpdatesMain">
        <section className="workspaceUpdatesSection">
          <button
            type="button"
            className={`workspaceMyStatusCard ${activeMyStatus ? 'workspaceMyStatusCard--active' : ''}`}
            onClick={openStatusCard}
          >
            <div
              className={`workspaceMyStatusAvatarShell ${
                activeMyStatus?.isViewedByMe ? 'workspaceMyStatusAvatarShell--viewed' : ''
              }`}
            >
              <img
                className="workspaceMyStatusAvatar"
                src={firstNonEmpty(currentProfile?.avatarUrl, sampleImage)}
                alt={firstNonEmpty(currentProfile?.displayName, 'Student')}
              />
            </div>
            <div className="workspaceMyStatusCopy">
              <strong>My status</strong>
              <span>{myStatusBody}</span>
            </div>
            <div className="workspaceMyStatusAction">
              <FontAwesomeIcon icon={faPlus} />
            </div>
          </button>
        </section>

        <section className="workspaceUpdatesSection">
          <div className="workspaceUpdatesSectionTitle">
            <h2>Recent updates</h2>
            <span>{recentStatuses.length} stories</span>
          </div>

          {loadingFeed && (
            <div className="workspaceUpdatesStateCard">
              <div className="workspaceUpdatesSpinner" />
              <p>Loading updates...</p>
            </div>
          )}

          {!loadingFeed && feedError && recentStatuses.length === 0 && !activeMyStatus && (
            <div className="workspaceUpdatesStateCard">
              <p>{feedError}</p>
            </div>
          )}

          {!loadingFeed && !feedError && recentStatuses.length === 0 && !activeMyStatus && (
            <div className="workspaceUpdatesStateCard">
              <h3>No updates yet</h3>
              <p>You can be the first to share a story.</p>
            </div>
          )}

          <div className="workspaceUpdatesStories">
            {recentStatuses.map((story) => {
              const badge =
                story.activeCount > 1
                  ? `${story.activeCount} updates`
                  : story.isViewedByMe
                    ? 'Seen'
                    : 'New'

              return (
                <button
                  key={story.statusId}
                  type="button"
                  className="workspaceStatusRow"
                  onClick={() => openStatusViewer(story.statusId, story.userId)}
                >
                  <div
                    className={`workspaceStatusRowAvatarShell ${
                      story.isViewedByMe ? 'workspaceStatusRowAvatarShell--viewed' : ''
                    }`}
                  >
                    <img
                      className="workspaceStatusRowAvatar"
                      src={firstNonEmpty(story.avatarUrl, sampleImage)}
                      alt={story.displayName}
                    />
                  </div>

                  <div className="workspaceStatusRowCopy">
                    <strong>{story.displayName}</strong>
                    <span>{formatStoryTime(story.createdAtMs)}</span>
                    <p>{firstNonEmpty(story.course, 'Course not set')}</p>
                  </div>

                  <div className="workspaceStatusRowBadge">{badge}</div>
                </button>
              )
            })}
          </div>
        </section>
      </main>

      <button
        type="button"
        className="workspaceUpdatesFab"
        onClick={openComposer}
        aria-label="Create status"
      >
        <FontAwesomeIcon icon={faPlus} />
      </button>

      {composerOpen && (
        <div className="workspaceStatusScrim" role="presentation" onClick={closeComposer}>
          <div
            className="workspaceStatusComposer"
            role="dialog"
            aria-modal="true"
            aria-label="Create status"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="workspaceStatusComposerHeader">
              <button type="button" className="workspaceStatusIconButton" onClick={closeComposer} aria-label="Close composer">
                <FontAwesomeIcon icon={faXmark} />
              </button>
              <div>
                <strong>Create status</strong>
                <p>Post a story with text, photo, or video.</p>
              </div>
              <button
                type="button"
                className="workspaceStatusPrimaryButton"
                onClick={publishTextStatus}
                disabled={publishing}
              >
                {publishing ? `Posting ${publishProgress}%` : 'Post'}
              </button>
            </div>

            <div className="workspaceStatusProfileCard">
              <div className="workspaceStatusProfileAvatarShell">
                <img
                  className="workspaceStatusProfileAvatar"
                  src={firstNonEmpty(currentProfile?.avatarUrl, sampleImage)}
                  alt={firstNonEmpty(currentProfile?.displayName, 'Student')}
                />
              </div>
              <div className="workspaceStatusProfileCopy">
                <strong>{firstNonEmpty(currentProfile?.displayName, 'Student')}</strong>
                <span>{firstNonEmpty(currentProfile?.course, 'Course not set')}</span>
              </div>
            </div>

            <label className="workspaceStatusTextareaShell" htmlFor="workspaceStatusCaption">
              <span>Caption</span>
              <textarea
                id="workspaceStatusCaption"
                value={composerCaption}
                onChange={(event) => setComposerCaption(event.target.value)}
                placeholder="What's on your mind?"
              />
            </label>

            <div className="workspaceStatusColorRow">
              {STATUS_COLORS.map((color, index) => {
                const active = index === composerColorIndex
                return (
                  <button
                    key={color}
                    type="button"
                    className={`workspaceStatusColorChip ${active ? 'workspaceStatusColorChip--active' : ''}`}
                    style={{ backgroundColor: color }}
                    onClick={() => setComposerColorIndex(index)}
                    aria-label={`Select status color ${index + 1}`}
                  />
                )
              })}
            </div>

            {selectedMediaPreviewUrl && (
              <div className="workspaceStatusMediaPreview">
                {selectedMediaType === 'video' ? (
                  <video src={selectedMediaPreviewUrl} controls playsInline />
                ) : (
                  <img src={selectedMediaPreviewUrl} alt="Status preview" />
                )}
              </div>
            )}

            <div className="workspaceStatusComposerActions">
              <button type="button" className="workspaceStatusSecondaryButton" onClick={triggerMediaPicker} disabled={publishing}>
                Choose media
              </button>
              <button type="button" className="workspaceStatusSecondaryButton" onClick={clearSelectedMedia} disabled={publishing || !selectedMediaFile}>
                Clear media
              </button>
            </div>

            {publishing && (
              <div className="workspaceStatusProgressBar">
                <span style={{ width: `${publishProgress}%` }} />
              </div>
            )}
          </div>
        </div>
      )}

      {viewerOpen && (
        <div className="workspaceStatusScrim workspaceStatusScrim--dark" role="presentation" onClick={closeViewer}>
          <div
            className="workspaceStatusViewer"
            role="dialog"
            aria-modal="true"
            aria-label="Status viewer"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="workspaceStatusViewerProgressRow">
              {viewerStories.map((story, index) => (
                <span
                  key={story.statusId}
                  className={index <= viewerIndex ? 'workspaceStatusViewerProgress workspaceStatusViewerProgress--active' : 'workspaceStatusViewerProgress'}
                />
              ))}
            </div>

            <div className="workspaceStatusViewerHeader">
              <div className="workspaceStatusViewerProfile">
                <img src={firstNonEmpty(currentStory?.avatarUrl, sampleImage)} alt={currentStory?.displayName || 'Student'} />
                <div>
                  <strong>{firstNonEmpty(currentStory?.displayName, 'Student')}</strong>
                  <span>
                    {firstNonEmpty(currentStory?.course, 'Course not set')}
                    {' • '}
                    {currentStory ? formatStoryTime(currentStory.createdAtMs) : 'Just now'}
                  </span>
                </div>
              </div>

              <div className="workspaceStatusViewerActions">
                {currentStory?.isMine && (
                  <button type="button" className="workspaceStatusViewerIcon" onClick={openViewers} aria-label="View viewers">
                    <FontAwesomeIcon icon={faEye} />
                  </button>
                )}
                {currentStory?.isMine && (
                  <button
                    type="button"
                    className="workspaceStatusViewerIcon workspaceStatusViewerIcon--danger"
                    onClick={confirmAndDeleteCurrentStory}
                    aria-label="Delete status"
                  >
                    <FontAwesomeIcon icon={faTrash} />
                  </button>
                )}
                <button type="button" className="workspaceStatusViewerIcon" onClick={closeViewer} aria-label="Close viewer">
                  <FontAwesomeIcon icon={faXmark} />
                </button>
              </div>
            </div>

            <div className="workspaceStatusViewerStage">
              <button
                type="button"
                className="workspaceStatusViewerNav workspaceStatusViewerNav--left"
                onClick={goToPreviousStory}
                aria-label="Previous status"
                disabled={viewerIndex <= 0}
              >
                <FontAwesomeIcon icon={faChevronLeft} />
              </button>

              <div className="workspaceStatusViewerCanvas">
                {viewerLoading && (
                  <div className="workspaceStatusViewerEmpty">
                    <p>Loading story...</p>
                  </div>
                )}

                {!viewerLoading && !selectedViewerStory && (
                  <div className="workspaceStatusViewerEmpty">
                    <p>No story to show.</p>
                  </div>
                )}

                {!viewerLoading && selectedViewerStory && renderStoryContent(selectedViewerStory)}
              </div>

              <button
                type="button"
                className="workspaceStatusViewerNav workspaceStatusViewerNav--right"
                onClick={goToNextStory}
                aria-label="Next status"
                disabled={viewerIndex >= viewerStories.length - 1}
              >
                <FontAwesomeIcon icon={faChevronRight} />
              </button>
            </div>

            <div className="workspaceStatusViewerFooter">
              <div className="workspaceStatusViewerMeta">
                <strong>
                  {currentStory ? `${currentStory.activeCount > 1 ? `${currentStory.activeCount} updates` : 'Update'}` : 'Update'}
                </strong>
                <span>
                  {currentStory ? getRelativeMeta(currentStory) : 'Just now'}
                </span>
              </div>

              <div className="workspaceStatusViewerHint">
                Tap the sides or use the arrows to move between stories.
              </div>
            </div>
          </div>
        </div>
      )}

      {viewersOpen && (
        <div className="workspaceStatusScrim" role="presentation" onClick={closeViewers}>
          <div
            className="workspaceStatusViewers"
            role="dialog"
            aria-modal="true"
            aria-label="Status viewers"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="workspaceStatusViewersHeader">
              <div>
                <strong>Viewers</strong>
                <p>{viewersStatus?.displayName || 'This status'}</p>
              </div>
              <button type="button" className="workspaceStatusIconButton" onClick={closeViewers} aria-label="Close viewers">
                <FontAwesomeIcon icon={faXmark} />
              </button>
            </div>

            <div className="workspaceStatusViewersList">
              {viewersLoading && (
                <div className="workspaceStatusViewerEmpty">
                  <p>Loading viewers...</p>
                </div>
              )}

              {!viewersLoading && viewers.length === 0 && (
                <div className="workspaceStatusViewerEmpty">
                  <p>No viewers yet.</p>
                </div>
              )}

              {viewers.map((viewer) => (
                <div key={viewer.id} className="workspaceStatusViewerRow">
                  <img src={firstNonEmpty(viewer.avatarUrl, sampleImage)} alt={viewer.displayName} />
                  <div className="workspaceStatusViewerRowCopy">
                    <strong>{viewer.displayName}</strong>
                    <span>{firstNonEmpty(viewer.course, 'Course not set')}</span>
                  </div>
                  <div className="workspaceStatusViewerRowTime">
                    {viewer.viewedAtMs ? formatStoryTime(viewer.viewedAtMs) : ''}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default WorkspaceUpdatesPage
